/**
 * AI image generation dialog for the creative-asset library (#4332).
 *
 * Collects a prompt, aspect and count, calls POST /assets/generate via
 * useGenerateCreativeAssetsMutation and closes on success — the mutation
 * prepends the new rows into the library caches, so the hosting gallery
 * updates without a refetch. Generation is slow (30-90s per image); the
 * pending state says so instead of pretending to be instant.
 */

import { useState } from 'react';
import { Sparkles } from 'lucide-react';

import { FieldError } from '@/components/glitch-ui/field-error';
import { SegmentedControl } from '@/components/glitch-ui/segmented-control';
import { Spinner } from '@/components/glitch-ui/spinner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { PlatformLogo } from '@/lib/performance/platform';
import { useGenerateCreativeAssetsMutation } from '@/lib/query-options';
import type { AdPlatform } from '@/lib/types';

const IMAGE_COUNT_OPTIONS = [1, 2, 3, 4];

interface GenerateImageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GenerateImageDialog({ open, onOpenChange }: GenerateImageDialogProps) {
  const { toast } = useToast();
  const generate = useGenerateCreativeAssetsMutation();

  const [prompt, setPrompt] = useState('');
  const [aspect, setAspect] = useState<'square' | 'story'>('square');
  const [count, setCount] = useState(1);
  const [platform, setPlatform] = useState<AdPlatform>('META');
  const [promptError, setPromptError] = useState<string | undefined>(undefined);

  const handleOpenChange = (next: boolean) => {
    if (!next && generate.isPending) return; // don't abandon an in-flight generation by accident
    if (next) setPromptError(undefined);
    onOpenChange(next);
  };

  const handleGenerate = async () => {
    // Tell-on-click (#5857): the button stays enabled; clicking with a missing
    // prompt marks the field instead of sitting silently disabled.
    if (prompt.trim().length < 3) {
      setPromptError('Required');
      return;
    }
    try {
      await generate.mutateAsync({ prompt: prompt.trim(), aspect, n: count, platform });
      setPrompt('');
      onOpenChange(false);
    } catch (e) {
      toast({
        title: 'Image generation failed',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Generate Images</DialogTitle>
          <DialogDescription>
            Images are generated and added to the asset library.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Prompt</Label>
              <FieldError message={promptError} />
            </div>
            <Textarea
              value={prompt}
              error={!!promptError}
              maxLength={2000}
              rows={3}
              placeholder="e.g. A clean product shot of running shoes on a pastel background"
              onChange={(e) => {
                setPrompt(e.target.value);
                setPromptError(undefined);
              }}
              disabled={generate.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Platform</Label>
            <div>
              <SegmentedControl
                ariaLabel="Platform"
                value={platform}
                onChange={setPlatform}
                disabled={generate.isPending}
                options={[
                  { value: 'META', label: 'Meta', icon: <PlatformLogo platform="meta" className="h-3.5 w-3.5 shrink-0" /> },
                  { value: 'GOOGLE', label: 'Google', icon: <PlatformLogo platform="google" className="h-3.5 w-3.5 shrink-0" /> },
                ]}
              />
            </div>
          </div>

          <div className="flex items-end justify-between gap-4">
            <div className="space-y-1.5">
              <Label>Format</Label>
              <div>
                <SegmentedControl
                  ariaLabel="Format"
                  value={aspect}
                  onChange={setAspect}
                  disabled={generate.isPending}
                  options={[
                    { value: 'square', label: 'Square' },
                    { value: 'story', label: 'Story (9:16)' },
                  ]}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Images</Label>
              <Select
                value={String(count)}
                onValueChange={(v) => setCount(Number(v))}
                disabled={generate.isPending}
              >
                <SelectTrigger className="w-20 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGE_COUNT_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            size="lg"
            className="w-full py-6 bg-[#290BD9] hover:bg-[#2309C5]"
            onClick={handleGenerate}
            disabled={generate.isPending}
          >
            {generate.isPending ? (
              <>
                <Spinner size="sm" className="mr-2" />
                GENERATING… THIS CAN TAKE A MINUTE OR TWO
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                GENERATE
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
